import React, { useState } from 'react';
import { useDarkMode } from '../../context/DarkModeContext';
import { PhoneIcon, ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { OTPInput } from '@shared/components';
import { useWhatsAppAuth } from '../hooks';
import { Button, InputField } from '@shared/components';

interface WhatsAppSignInProps {
  onBack: () => void;
  primaryColor: string;
}

const WhatsAppSignIn = ({
  onBack,
  primaryColor
}: WhatsAppSignInProps) => {
  const { isDarkMode } = useDarkMode();
  const [step, setStep] = useState<1 | 2>(1);
  const [userId, setUserId] = useState<string>('');
  const [phoneError, setPhoneError] = useState('');

  const { requestWhatsAppVerification, verifyWhatsAppCode, isLoading, isSendingCode } = useWhatsAppAuth();
  // Canonical E.164 phone (e.g. +447712345678) captured by the country-aware field.
  const [phone, setPhone] = useState('');

  // Validation function
  const validatePhone = () => {
    const phoneDigits = phone.replace(/\D/g, '');
    if (!phoneDigits) {
      setPhoneError('Phone number is required');
      return false;
    } else if (phoneDigits.length < 10) {
      setPhoneError('Please enter a valid phone number');
      return false;
    }
    setPhoneError('');
    return true;
  };

  // Check if form is valid for button state
  const isFormValid = phone.replace(/\D/g, '').length >= 10 && !phoneError;

  const handlePhoneInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setPhone(e.target.value);

    // Clear error when user starts typing
    if (phoneError) {
      setPhoneError('');
    }
  };

  const handleSubmitPhone = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePhone()) {
      return;
    }

    const result = await requestWhatsAppVerification(phone);
    if (result.success) {
      setUserId(result.userId!);
      setStep(2);
    }
  };

  const handleVerifyCode = async (code: string) => {
    const result = await verifyWhatsAppCode(code, userId);
    if (result.success) {
      // Login successful
      return;
    }
  };

  const handleResendCode = async () => {
    if (!isSendingCode) {
      const result = await requestWhatsAppVerification(phone);
      if (result.success) {
        setUserId(result.userId!);
      }
    }
  };

  return (
    <div className="w-full max-w-md">
      <button
        type="button"
        onClick={step === 1 ? onBack : () => setStep(1)}
        className="mb-4 flex items-center text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-200 dark:hover:text-white"
      >
        <ArrowLeftIcon className="h-4 w-4 mr-1" />
        {step === 1 ? 'Back to login options' : 'Back to phone number'}
      </button>

      {step === 1 ? (
        <>
          <h2 className="text-2xl font-bold mb-6 text-center text-gray-900 dark:text-gray-100">
            Sign in with WhatsApp
          </h2>
          <form onSubmit={handleSubmitPhone} className="space-y-6">
            <div>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Enter your phone number to receive a verification code via
                WhatsApp.
              </p>
              <div className="space-y-2">
                <InputField
                  label="Phone Number"
                  name="phone"
                  type="phone"
                  value={phone}
                  onChange={handlePhoneInputChange}
                  required
                  fullWidth
                  darkMode={isDarkMode}
                  className={phoneError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
                />
                {phoneError && (
                  <p className="text-sm text-red-600 mt-1">{phoneError}</p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Select your country, then enter your number to receive the code.
                </p>
              </div>
            </div>
                        <Button
              type="submit"
              variant="primary"
              disabled={!isFormValid}
              isLoading={isSendingCode}
              fullWidth
              className="flex items-center justify-center"
            >
              {isSendingCode ? 'Sending...' : (
                <>
                  Continue
                  <ArrowRightIcon className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>
        </>
      ) : (
        <OTPInput
          onComplete={handleVerifyCode}
          onResend={handleResendCode}
          title="Verify your WhatsApp"
          description={`We've sent a verification code to ${phone} via WhatsApp. Please enter the code below to sign in.`}
          verifyText="Verify & Sign In"
          primaryColor={primaryColor}
          isLoading={isLoading}
          disabled={isSendingCode}
          isResending={isSendingCode}
        />
      )}
    </div>
  );
};

export default WhatsAppSignIn;
